import { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie';
import styles from './index.module.scss';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import newScreen4Background1 from '@/assets/home/new-screen4-background1.png';
import newScreen4Background2 from '@/assets/home/new-screen4-background2.png';
import newScreen4Ai from '@/assets/home/new-screen4-ai.png';
import buttonLeft1 from '@/assets/home/new-screen5-button-left1.png';
import buttonRight1 from '@/assets/home/new-screen5-button-right1.png';
import buttonRight2 from '@/assets/home/new-screen5-button-right2.png';
import buttonLottieData from '@/assets/lottie/button-data.json';

const buttonLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: buttonLottieData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

interface CarouselItem {
  id: number;
  title: string;
  description: string;
  buttonText: string;
  backgroundImage?: string;
  linkTo?: string;
}

export default function Screen4() {
  const navigate = useNavigate();
  const slideInRef = useScreenSlideIn({
    triggerSlideRatio: 0.8,
    animationClassName: styles['screen4-anim-start'],
    triggerOnce: true,
  });

  const carouselItems: CarouselItem[] = [
    {
      id: 1,
      title: '爱学AI学习智能体',
      description: '终身学习的个性化成长陪伴',
      buttonText: '了解详情',
      backgroundImage: newScreen4Background1,
      linkTo: '/aixue-ai',
    },
    {
      id: 2,
      title: '爱学AI低龄段产品矩阵',
      description: '思维、人文、双语全面覆盖',
      buttonText: '了解详情',
      backgroundImage: newScreen4Background2,
      linkTo: '/aixue-phonics',
    },
  ];

  const total = carouselItems.length;
  const [slideIndex, setSlideIndex] = useState(1);
  const trackRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const logicalIndex = slideIndex === 1 || slideIndex === 3 ? 0 : 1;

  const goToSlide = useCallback(
    (index: number) => {
      if (index >= 0 && index < total) setSlideIndex(index === 0 ? 1 : 2);
    },
    [total],
  );

  const goNext = useCallback(() => {
    if (slideIndex === 1) setSlideIndex(2);
    else if (slideIndex === 2) setSlideIndex(3);
    else if (slideIndex === 3) {
      const track = trackRef.current;
      if (track) track.style.transition = 'none';
      setSlideIndex(1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (track) track.style.transition = '';
          setSlideIndex(2);
        });
      });
    }
  }, [slideIndex]);

  const goPrev = useCallback(() => {
    if (slideIndex === 2) setSlideIndex(1);
    else if (slideIndex === 1) setSlideIndex(0);
    else if (slideIndex === 0) {
      const track = trackRef.current;
      if (track) track.style.transition = 'none';
      setSlideIndex(2);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (track) track.style.transition = '';
          setSlideIndex(1);
        });
      });
    }
  }, [slideIndex]);

  const handleTrackTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.target !== trackRef.current || e.propertyName !== 'transform') return;
      const track = trackRef.current;
      if (!track) return;
      if (slideIndex === 0) {
        track.style.transition = 'none';
        setSlideIndex(2);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
          });
        });
      } else if (slideIndex === 3) {
        track.style.transition = 'none';
        setSlideIndex(1);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
          });
        });
      }
    },
    [slideIndex],
  );

  const goNextRef = useRef(goNext);
  const goPrevRef = useRef(goPrev);
  goNextRef.current = goNext;
  goPrevRef.current = goPrev;

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    let lastT = 0;
    let acc = 0;
    let lastTriggerAt = 0;
    const THRESHOLD = 80;
    const WINDOW = 400;
    const COOLDOWN_MS = 700;

    const handleGestureStart = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleGestureChange = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleGestureEnd = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleDocumentGestureStart = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const handleDocumentGestureChange = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const handleDocumentGestureEnd = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const isHorizontalScroll =
        Math.abs(deltaX) > Math.abs(deltaY) ||
        (Math.abs(deltaX) > 0 && Math.abs(deltaY) < 3) ||
        (Math.abs(deltaX) > Math.abs(deltaY) * 0.3 && Math.abs(deltaY) < 10);

      if (isHorizontalScroll && !e.defaultPrevented) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!isHorizontalScroll) return;

      const now = Date.now();
      const dt = lastT ? now - lastT : 0;
      const windowReset = dt > WINDOW;
      if (windowReset) acc = 0;
      lastT = now;
      acc += deltaX;
      if (Math.abs(acc) < THRESHOLD) return;
      const nowForTrigger = Date.now();
      if (nowForTrigger - lastTriggerAt < COOLDOWN_MS) {
        acc = 0;
        return;
      }
      lastTriggerAt = nowForTrigger;
      acc = 0;
      if (deltaX > 0) goNextRef.current();
      else goPrevRef.current();
    };

    container.addEventListener('gesturestart', handleGestureStart, {
      passive: false,
      capture: true,
    });
    container.addEventListener('gesturechange', handleGestureChange, {
      passive: false,
      capture: true,
    });
    container.addEventListener('gestureend', handleGestureEnd, {
      passive: false,
      capture: true,
    });
    container.addEventListener('wheel', handleWheel, { passive: false, capture: false });
    document.addEventListener('gesturestart', handleDocumentGestureStart, {
      passive: false,
      capture: true,
    });
    document.addEventListener('gesturechange', handleDocumentGestureChange, {
      passive: false,
      capture: true,
    });
    document.addEventListener('gestureend', handleDocumentGestureEnd, {
      passive: false,
      capture: true,
    });

    return () => {
      container.removeEventListener('gesturestart', handleGestureStart, { capture: true });
      container.removeEventListener('gesturechange', handleGestureChange, { capture: true });
      container.removeEventListener('gestureend', handleGestureEnd, { capture: true });
      container.removeEventListener('wheel', handleWheel, { capture: false });
      document.removeEventListener('gesturestart', handleDocumentGestureStart, { capture: true });
      document.removeEventListener('gesturechange', handleDocumentGestureChange, {
        capture: true,
      });
      document.removeEventListener('gestureend', handleDocumentGestureEnd, { capture: true });
    };
  }, []);

  const scrollToTopOnNextPage = () => {
    setTimeout(() => {
      const selectors = [
        '[data-scroll-root="aixue-phonics"]',
        '[data-scroll-root="aixue-ai-scroll"]',
      ];
      for (const sel of selectors) {
        const node = document.querySelector(sel) as HTMLElement | null;
        if (node) node.scrollTo({ top: 0, behavior: 'auto' });
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 50);
  };

  const handleButtonClick = (item: CarouselItem) => {
    if (item.linkTo) {
      navigate(item.linkTo);
      scrollToTopOnNextPage();
    }
  };

  return (
    <section className={styles['screen4-cover']} ref={slideInRef.ref}>
      <div className={styles['screen4-wrap']}>
        <div className={styles['screen4-title-container']}>
          <h2 className={styles['screen4-title']}>AI教育产品</h2>
          <p className={styles['screen4-sub']}>
            爱学致力于利用AI技术，为用户提供专业、个性化、高质量的学习产品
          </p>
        </div>

        <div className={styles['screen4-carousel-wrapper']} ref={carouselRef}>
          <div className={styles['screen4-carousel']}>
            <div
              ref={trackRef}
              className={`${styles['screen4-carousel-track']} ${styles['screen4-carousel-track-infinite']}`}
              style={{
                transform: `translateX(-${slideIndex * 25}%)`,
              }}
              onTransitionEnd={handleTrackTransitionEnd}
            >
              {[carouselItems[1], carouselItems[0], carouselItems[1], carouselItems[0]].map(
                (item, index) => (
                  <div key={`${item.id}-${index}`} className={styles['screen4-carousel-item']}>
                    <div className={styles['screen4-card-content']}>
                      <div className={styles['screen4-card-left']}>
                        <h3 className={styles['screen4-card-title']}>{item.title}</h3>
                        <p className={styles['screen4-card-desc']}>{item.description}</p>
                        <button
                          className={styles['screen4-card-button']}
                          onClick={() => handleButtonClick(item)}
                          aria-label={item.buttonText}
                        >
                          <Lottie
                            options={buttonLottieOptions}
                            width="100%"
                            height="100%"
                            style={{ pointerEvents: 'none' }}
                          />
                        </button>
                      </div>
                      <div className={styles['screen4-card-right']}></div>
                    </div>
                    {item.backgroundImage && (
                      <div
                        className={styles['screen4-card-illustration']}
                        style={{
                          backgroundImage: `url(${item.backgroundImage})`,
                        }}
                      >
                        <img
                          src={newScreen4Ai}
                          alt=""
                          className={styles['screen4-ai-icon']}
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
          <div className={styles['screen4-carousel-indicators']}>
            {carouselItems.map((_, index) => (
              <div
                key={index}
                className={styles['screen4-carousel-indicator-hitarea']}
                onClick={() => goToSlide(index)}
                onKeyDown={(e) => e.key === 'Enter' && goToSlide(index)}
                role="button"
                tabIndex={0}
                aria-label={`切换到第 ${index + 1} 页`}
              >
                <span
                  className={`${styles['screen4-carousel-indicator']} ${
                    logicalIndex === index ? styles['screen4-carousel-indicator-active'] : ''
                  }`}
                  aria-hidden
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            className={styles['screen4-button-left']}
            onClick={goPrev}
            aria-label="上一张"
          >
            <img
              src={buttonLeft1}
              alt=""
              className={styles['screen4-button-img-default']}
              loading="lazy"
            />
            <img
              src={buttonRight2}
              alt=""
              className={`${styles['screen4-button-img-hover']} ${styles['screen4-button-left-hover']}`}
              loading="lazy"
            />
          </button>
          <button
            type="button"
            className={styles['screen4-button-right']}
            onClick={goNext}
            aria-label="下一张"
          >
            <img
              src={buttonRight1}
              alt=""
              className={styles['screen4-button-img-default']}
              loading="lazy"
            />
            <img
              src={buttonRight2}
              alt=""
              className={styles['screen4-button-img-hover']}
              loading="lazy"
            />
          </button>
        </div>
      </div>
    </section>
  );
}
